import React, { useMemo } from 'react';
import { 
    Box, Typography, Paper, Grid, Card, CardContent 
} from '@mui/material';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import dayjs from 'dayjs';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const BudgetVisuals = ({ data = [], categories = [], exchangeRates = {} }) => {
    const { t } = useTranslation();
    const currentLanguage = i18n.language;

    // Helper for currency conversion
    const convertToRON = useMemo(() => (item) => {
        const amount = parseFloat(item.amount);
        if (isNaN(amount)) return 0;
        
        const currency = item.currency || 'RON';
        if (currency === 'RON') return amount;
        
        const rate = exchangeRates[currency.toLowerCase()];
        if (rate && !isNaN(rate)) {
            return amount * parseFloat(rate);
        }
        return amount;
    }, [exchangeRates]);

    // Monthly data aggregation
    const monthlyData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        const map = data.reduce((acc, item) => {
            const month = dayjs(item.date).format('YYYY-MM');
            if (!acc[month]) acc[month] = 0;
            acc[month] += convertToRON(item);
            return acc;
        }, {});

        return Object.keys(map).sort().map(month => ({
            name: month,
            amount: map[month]
        }));
    }, [data, convertToRON]);

    // Category data aggregation
    const categoryData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        const map = data.reduce((acc, item) => {
            const cat = categories.find(c => c.id === item.category);
            const label = cat ? cat.label : item.category;
            if (!acc[label]) acc[label] = 0;
            acc[label] += convertToRON(item);
            return acc;
        }, {});

        return Object.keys(map).map(label => ({
            name: label,
            value: map[label]
        }));
    }, [data, categories, convertToRON]);

    // Yearly data aggregation
    const yearlyData = useMemo(() => {
        const map = data.reduce((acc, item) => {
            const year = dayjs(item.date).format('YYYY');
            if (!acc[year]) acc[year] = 0;
            acc[year] += convertToRON(item);
            return acc;
        }, {});

        return Object.keys(map).sort().map(year => ({
            name: year,
            amount: map[year]
        }));
    }, [data, convertToRON]);

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>{t('FamilyManager.Budget.VisualsTitle', 'Költségvetési Kimutatások')}</Typography>
            
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom>{t('FamilyManager.Budget.MonthlySpending', 'Havi költés')} (RON)</Typography>
                            <Box sx={{ height: 300, width: '100%' }}>
                                {monthlyData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={monthlyData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis tickFormatter={(value) => value.toLocaleString(currentLanguage)} />
                                            <Tooltip 
                                                formatter={(value) => value.toLocaleString(currentLanguage) + ' RON'}
                                                isAnimationActive={false}
                                                cursor={{ fill: 'transparent' }}
                                                trigger="click"
                                            />
                                            <Bar 
                                                dataKey="amount" 
                                                fill="#8884d8" 
                                                name={t('FamilyManager.Budget.Amount', 'Összeg')} 
                                                isAnimationActive={false}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                        <Typography color="textSecondary">{t('FamilyManager.Budget.NoData', 'Nincs megjeleníthető adat')}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom>{t('FamilyManager.Budget.ByCategory', 'Kategóriák szerint')} (RON)</Typography>
                            <Box sx={{ height: 300, width: '100%' }}>
                                {categoryData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={categoryData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                                isAnimationActive={false}
                                            >
                                                {categoryData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip 
                                                formatter={(value) => value.toLocaleString(currentLanguage) + ' RON'}
                                                isAnimationActive={false}
                                                cursor={{ fill: 'transparent' }}
                                                trigger="click"
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                        <Typography color="textSecondary">{t('FamilyManager.Budget.NoData', 'Nincs megjeleníthető adat')}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom>{t('FamilyManager.Budget.YearlySpending', 'Éves költés')} (RON)</Typography>
                            <Box sx={{ height: 300, width: '100%' }}>
                                {yearlyData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={yearlyData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis tickFormatter={(value) => value.toLocaleString(currentLanguage)} />
                                            <Tooltip 
                                                formatter={(value) => value.toLocaleString(currentLanguage) + ' RON'}
                                                isAnimationActive={false}
                                                cursor={{ fill: 'transparent' }}
                                                trigger="click"
                                            />
                                            <Bar 
                                                dataKey="amount" 
                                                fill="#82ca9d" 
                                                name={t('FamilyManager.Budget.Amount', 'Összeg')} 
                                                isAnimationActive={false}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                        <Typography color="textSecondary">{t('FamilyManager.Budget.NoData', 'Nincs megjeleníthető adat')}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default BudgetVisuals;
